/**
 * New console — display preferences.
 *
 * Theme mode, accent, corner style, text size, row density, contrast, typeface,
 * motion, link underlining, striped rows, the focus ring and status shapes. All of
 * it is applied as attributes and two custom properties, so every screen responds
 * without a single component knowing these settings exist.
 *
 * Stored per browser rather than per account on purpose. These are properties of the
 * screen someone is sitting at — a wall display in a clinic and the same person's
 * laptop want different answers — and there is no endpoint for them anyway.
 *
 * Everything reads and writes through try/catch: in a private window or with site
 * data blocked, localStorage throws on access rather than returning null, and a
 * console that will not start because it could not read a font preference would be a
 * far worse failure than the preference not sticking.
 */
const KEY = 'nt.prefs';

export const DEFAULTS = {
  themeMode: 'system', // system | light | dark — system follows the operating system
  accent: 'indigo',    // indigo | violet | teal | amber | rose | graphite
  corners: 'rounded',  // rounded | soft | square
  fontScale: 1,        // 0.85 … 1.5, in steps of 0.05
  density: 'cosy',     // compact | cosy | roomy
  contrast: 'normal',  // normal | high
  typeface: 'system',  // system | legible
  motion: 'full',      // full | reduced
  underline: 'off',    // off | on
  zebra: 'off',        // off | on — striped table rows
  focusRing: 'auto',   // auto (keyboard only) | always
  statusShapes: 'off', // off | on — a glyph on each badge as well as its colour
  railMini: false,     // the navigation collapsed to icons only
  tips: 'on',          // on | off — the ⓘ marks that explain what a figure counts
  hoverHints: 'on',    // on | off — the text shown by resting on a control
  dateFormat: 'medium',// how every date is written — see dates.js DATE_FORMATS
  autoLock: 0          // minutes of inactivity before the console locks; 0 never
};

/** The accent presets the panel offers, and the colour each swatch shows. */
export const ACCENTS = [
  { value: 'indigo', label: 'Indigo', swatch: '#4338CA' },
  { value: 'violet', label: 'Violet', swatch: '#6D28D9' },
  { value: 'teal', label: 'Teal', swatch: '#0F766E' },
  { value: 'amber', label: 'Amber', swatch: '#B45309' },
  { value: 'rose', label: 'Rose', swatch: '#BE123C' },
  { value: 'graphite', label: 'Graphite', swatch: '#3E4666' }
];

/** Text size runs as a slider, so the ends and the step live in one place. */
export const SCALE = { min: 0.85, max: 1.5, step: 0.05 };

export const clampScale = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 1;
  return Math.min(SCALE.max, Math.max(SCALE.min, Math.round(n * 100) / 100));
};

export const readPrefs = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (e) {
    return { ...DEFAULTS };
  }
};

export const writePrefs = (prefs) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch (e) {
    /* the setting simply will not survive a reload */
  }
};

/**
 * Five of the six are attributes on the console root, so they cannot touch the
 * classic console still installed alongside. Attributes rather than classes because
 * the CSS then reads as what it is — `[data-density="compact"]` says more than
 * `.compact`.
 *
 * Text size is the exception and has to go on <html>. Every size in this console is
 * in rem, and rem resolves against the document root — so a font-size on `.nt` scaled
 * only the few elements that inherit it and left every heading, table cell and number
 * exactly as it was. The attribute changed and the page did not, which is precisely
 * what made the setting look broken.
 *
 * A percentage, not a pixel value: a reader who has already raised the default font
 * size in their browser keeps that as the baseline rather than having it silently
 * overwritten with 16px.
 */
export const applyPrefs = (root, prefs) => {
  if (!root) return;

  root.style.setProperty('--nt-font-scale', String(prefs.fontScale));
  root.setAttribute('data-density', prefs.density);
  root.setAttribute('data-contrast', prefs.contrast);
  root.setAttribute('data-typeface', prefs.typeface);
  root.setAttribute('data-motion', prefs.motion);
  root.setAttribute('data-underline', prefs.underline);
  root.setAttribute('data-accent', prefs.accent || 'indigo');
  root.setAttribute('data-corners', prefs.corners || 'rounded');
  root.setAttribute('data-zebra', prefs.zebra || 'off');
  root.setAttribute('data-focus', prefs.focusRing || 'auto');
  root.setAttribute('data-shapes', prefs.statusShapes || 'off');
  root.setAttribute('data-tips', prefs.tips || 'on');

  const scale = Number(prefs.fontScale) || 1;
  const html = document.documentElement;
  if (scale === 1) html.style.removeProperty('font-size');
  else html.style.fontSize = `${scale * 100}%`;
};

/**
 * Puts the document root back. <html> outlives this console, and the classic one has
 * no such setting — leaving it scaled would resize a UI with no control to undo it.
 */
export const clearRootScale = () => {
  document.documentElement.style.removeProperty('font-size');
};

/**
 * A screen changing a preference, rather than the appearance panel.
 *
 * The shell owns the copy that is actually applied, so a screen cannot simply write
 * localStorage and expect the page to follow — it would change on the next reload and
 * look broken until then. Writing and announcing in one call keeps the two in step,
 * and matches how the console already asks the shell for things (`nt:tour`,
 * `nt:appearance`). Returns the new preferences so the caller can hold them as state.
 */
export const updatePrefs = (patch) => {
  const next = { ...readPrefs(), ...patch };
  writePrefs(next);
  window.dispatchEvent(new CustomEvent('nt:prefs', { detail: next }));
  return next;
};

/**
 * The preferences as a component can read them mid-render.
 *
 * `readPrefs` parses localStorage, which is far more work than a tooltip should do on
 * a screen carrying forty of them. This keeps one parsed copy and swaps it when
 * Settings announces a change, so asking is cheap and never a render behind — the
 * shell re-renders on the same event, which is what puts the new answer on screen.
 */
let live = null;
export const livePrefs = () => {
  if (!live) live = readPrefs();
  return live;
};

if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('nt:prefs', (e) => {
    if (e.detail) live = { ...DEFAULTS, ...e.detail };
  });
}

/* ---- the screen lock ------------------------------------------------------------
   Kept in storage so a reload — the first thing anyone tries against a lock — comes
   back locked. Read by the shell, written by the shell and by signing out. */
const LOCK_KEY = 'nt.locked';

export const readLocked = () => {
  try {
    return window.localStorage.getItem(LOCK_KEY) === '1';
  } catch (e) {
    return false;
  }
};

export const writeLocked = (on) => {
  try {
    if (on) window.localStorage.setItem(LOCK_KEY, '1');
    else window.localStorage.removeItem(LOCK_KEY);
  } catch (e) {
    /* A console that will not lock because storage is blocked is still better than one
       that will not start. The lock then lasts as long as the page does. */
  }
};
